import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';

interface UseAsyncDataOptions {
  /** Skip the initial fetch (e.g. while a required param is still undefined). */
  enabled?: boolean;
  /** Error code passed to `logError` for traceable console output. */
  code?: string;
  /** Message shown in the toast when the request fails. */
  errorMessage?: string;
  /** Surface failures as a toast (default true). */
  toastOnError?: boolean;
}

interface UseAsyncDataResult<T> {
  data: T;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  setData: React.Dispatch<React.SetStateAction<T>>;
}

/**
 * Fetch-on-mount with the loading/error/toast handling every list page in this app
 * repeats by hand. `fetcher` must be stable (wrap it in `useCallback`).
 */
export function useAsyncData<T>(
  fetcher: () => Promise<T>,
  initialData: T,
  options: UseAsyncDataOptions = {}
): UseAsyncDataResult<T> {
  const {
    enabled = true,
    code = 'DATA_FETCH_FAILED',
    errorMessage = 'Failed to load data',
    toastOnError = true,
  } = options;

  const [data, setData] = useState<T>(initialData);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetcher());
    } catch (err) {
      const message = logError({ message: errorMessage, error: err, code });
      setError(message);
      if (toastOnError) toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [fetcher, code, errorMessage, toastOnError]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    refetch();
  }, [enabled, refetch]);

  return { data, loading, error, refetch, setData };
}

export default useAsyncData;
